import moment from "moment"
import type { CategoryBreakdown, JourneyDayItem, QuotationVersionItemModel } from "models/axiosModel"
import type {
	IQuotationPreviewV2CitiesView,
	IQuotationPreviewV2ItineraryRow,
	IQuotationPreviewV2PackagePrice,
	IQuotationPreviewV2PaxCounts,
	IQuotationPreviewV2VersionChip,
} from "./quotationPreviewV2Types"

export const MAX_VISIBLE_CITIES = 3

export const NOT_PROVIDED_TEXT = "Not provided"

const MEAL_SHORT: Record<number, string> = { 1: "B", 2: "L", 3: "D" }

const toCount = (value: unknown): number => Math.max(0, Number(value || 0) || 0)

const toNullableNumber = (value: unknown): number | null => {
	if (value === null || value === undefined || value === "") return null
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : null
}

export const textOrNotProvided = (value: unknown): string => String(value ?? "").trim() || NOT_PROVIDED_TEXT

export const formatUsd = (value: unknown): string =>
	`$${Math.round(Number(value || 0) || 0).toLocaleString("en-US")}`

export const formatSignedAmount = (value: number): string => {
	const rounded = Math.round(value)
	const sign = rounded > 0 ? "+" : rounded < 0 ? "-" : ""
	return `${sign}${Math.abs(rounded).toLocaleString("en-US")}`
}

export const buildTravelDatesText = (startDate?: string | null, numberOfNights?: number | null): string => {
	if (!startDate) return "-"
	const start = moment(startDate)
	if (!start.isValid()) return "-"
	const nights = toCount(numberOfNights)
	if (!nights) return start.format("DD/MM/YYYY")
	const end = start.clone().add(nights, "days")
	return `${start.format("DD/MM/YYYY")} – ${end.format("DD/MM/YYYY")}`
}

// Root creation time (first Draft), the same for every version in the chain.
export const buildCreationDateText = (quotation: any): string => {
	const raw = quotation?.created_at
	if (!raw) return "-"
	const value = moment(raw)
	return value.isValid() ? value.format("DD/MM/YYYY") : "-"
}

export const resolveAgencyName = (quotation: any): string =>
	quotation?.agency_name ||
	quotation?.agent?.agency_name ||
	quotation?.agency ||
	"-"

// Same source order as the existing preview: top-level lines first, then the ones nested on the quotation.
export const buildTextLines = (topLevel?: unknown, nested?: unknown): string[] => {
	const raw = Array.isArray(topLevel) ? topLevel : Array.isArray(nested) ? nested : []
	return raw.map((line) => String(line ?? "").trim()).filter(Boolean)
}

export const buildPaxCounts = (
	quotation: any,
	children: { flag_with_bed?: boolean }[] = [],
): IQuotationPreviewV2PaxCounts => {
	const adults = toCount(quotation?.number_of_adults)
	const extraBed = toCount(quotation?.number_of_extra_bed ?? quotation?.number_of_extra_beds)
	const childWithBed =
		quotation?.number_of_children_with_bed != null
			? toCount(quotation.number_of_children_with_bed)
			: children.filter((child) => child?.flag_with_bed).length
	const childWithoutBed =
		quotation?.number_of_children_without_bed != null
			? toCount(quotation.number_of_children_without_bed)
			: children.filter((child) => !child?.flag_with_bed).length
	return {
		adults,
		extraBed,
		childWithBed,
		childWithoutBed,
		total: adults + extraBed + childWithBed + childWithoutBed,
	}
}

// GET details `cities[]` only carries city ids, so names come from the journey days (first-seen order).
export const buildCitiesView = (
	days: JourneyDayItem[],
	maxVisible: number = MAX_VISIBLE_CITIES,
): IQuotationPreviewV2CitiesView => {
	const all: string[] = []
	days.forEach((day) => {
		const name = String(day?.city_name || "").trim()
		if (name && !all.some((city) => city.toLowerCase() === name.toLowerCase())) {
			all.push(name)
		}
	})
	return {
		visible: all.slice(0, maxVisible),
		hidden: all.slice(maxVisible),
		all,
	}
}

export const buildItineraryRows = (
	days: JourneyDayItem[],
	isEmptyHtml: (html: string) => boolean,
): IQuotationPreviewV2ItineraryRow[] =>
	days.map((day) => {
		const tripBlocks = (day.trips || [])
			.map((trip) => ({
				name: trip.day_trip_name || "",
				itineraryHtml: String((trip as any).day_trip_itinerary || ""),
			}))
			.filter((block) => block.name || !isEmptyHtml(block.itineraryHtml))
		const mealCodes = (day.meals || [])
			.slice()
			.sort((a, b) => Number(a.quotation_meal_type_id || 0) - Number(b.quotation_meal_type_id || 0))
			.map((meal) => MEAL_SHORT[Number(meal.quotation_meal_type_id || 0)])
			.filter(Boolean)
			.join(", ")
		const meals = day.flag_dinner_transfer ? (mealCodes ? `${mealCodes}, DT` : "DT") : mealCodes
		return {
			dayLabel: `Day ${day.day_number || ""}`,
			date: day.date ? moment(day.date).format("DD MMM YYYY") : "-",
			overnight: day.city_name || "-",
			tripBlocks,
			meals: meals || "—",
			guide: day.flag_guide ? "Yes" : "-",
		}
	})

// Oldest → newest, as the chips read left to right. The difference is against the parent version it was cloned from.
export const buildVersionChips = (
	versions: QuotationVersionItemModel[],
	activeQuotationId: string,
	isSameId: (left?: unknown, right?: unknown) => boolean,
): IQuotationPreviewV2VersionChip[] => {
	const sorted = [...versions].sort((a, b) => Number(a?.version_no || 0) - Number(b?.version_no || 0))
	const latestNo = sorted.length ? Number(sorted[sorted.length - 1]?.version_no || 0) : 0
	return sorted.map((version) => {
		const versionNo = Number(version?.version_no || 0)
		return {
			quotationId: String(version?.quotation_id || ""),
			versionNo,
			priceUsd: toNullableNumber(version?.total_price_usd),
			diffUsd: toNullableNumber(version?.price_difference_usd),
			flagLatest: versionNo === latestNo,
			flagActive: isSameId(version?.quotation_id, activeQuotationId),
		}
	})
}

// Per-pax selling price for each category, in the order the API sends them.
export const buildPackagePrices = (breakdown?: CategoryBreakdown[] | null): IQuotationPreviewV2PackagePrice[] =>
	(Array.isArray(breakdown) ? breakdown : []).map((row) => ({
		label: String(row?.category_name || "").trim() || "-",
		amountUsd: Number(row?.total_usd || 0) || 0,
	}))
